// Phase 2: Public Key Registry for signing verification

import { supabase } from '@/integrations/supabase/client';

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function exportSigningPublicKey(key: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey('spki', key);
  return arrayBufferToBase64(exported);
}

export async function importSigningPublicKey(base64: string): Promise<CryptoKey> {
  const buffer = base64ToArrayBuffer(base64);
  return crypto.subtle.importKey(
    'spki',
    buffer,
    { name: 'RSA-PSS', hash: 'SHA-256' },
    true,
    ['verify']
  );
}

export async function registerSigningPublicKey(userId: string, publicKey: CryptoKey) {
  const publicKeyBase64 = await exportSigningPublicKey(publicKey);

  const { error } = await (supabase as any)
    .from('user_public_keys')
    .upsert(
      {
        user_id: userId,
        public_key: publicKeyBase64,
        algorithm: 'RSA-PSS',
        version: 1,
      },
      { onConflict: 'user_id' }
    );

  if (error) throw error;
}

export async function fetchSigningPublicKey(userId: string): Promise<CryptoKey | null> {
  const { data, error } = await (supabase as any)
    .from('user_public_keys')
    .select('public_key')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data?.public_key) return null;
  return importSigningPublicKey(data.public_key);
}
